<template lang="pug">
modal-component(
  :title='tituloModal'
  width='680px'
  @close='$emit("close")'
)
  #modal-validacao-monitoramento(v-loading='isLoading')

    //- Cabeçalho com dados do monitoramento
    .dados-monitoramento
      .dado-item
        span.dado-label CAR:
        span.dado-valor {{ dados.codigoImovel || monitoramento.numeroDoCAR }}
      .dado-item
        span.dado-label Gerente Operacional:
        span.dado-valor {{ dados.nomeGerenteOperacional || '—' }}
      .dado-item
        span.dado-label Nº Monitoramento em análise:
        span.dado-valor {{ dados.numeroMonitoramento || '—' }}
      .dado-item
        span.dado-label Prazo original:
        span.dado-valor {{ prazoOriginalLabel }}

    el-divider

    //- Técnicos responsáveis
    .secao
      .secao-header
        span.secao-titulo Técnicos responsáveis
        button-component(
          v-if='!modoLeitura'
          label='Vincular / Desvincular'
          icon='mdi-account-multiple-plus'
          @click.native='showModalTecnicos = true'
        )
      .tecnicos-badges(v-if='tecnicosVinculados.length > 0')
        .tecnico-badge(
          v-for='tecnico in tecnicosVinculados'
          :key='tecnico.id'
        )
          button.tecnico-badge__remover(
            v-if='!modoLeitura'
            type='button'
            title='Remover técnico'
            @click='removerTecnico(tecnico.id)'
          )
            i.el-icon-close
          span.tecnico-badge__nome {{ tecnico.nome }}
      p.aviso-campo(v-else) Nenhum técnico vinculado
      p.erro-validacao(v-if='erros.tecnicos') {{ erros.tecnicos }}

    el-divider

    //- Upload de documentos
    .secao
      .secao-titulo Documentos *
      botao-upload-component(
        v-if='!modoLeitura'
        texto='Adicionar documentos'
        :limite='5'
        :fileList='fileListDisplay'
        :dadosExtra='uploadDadosExtra'
        @file-upload='onFileUpload'
        @file-removed='onFileRemoved'
      )
      .lista-arquivos(v-else-if='fileListDisplay.length > 0')
        button.arquivo-link(
          v-for='arquivo in fileListDisplay'
          :key='arquivo.name + arquivo.caminhoArquivo'
          type='button'
          @click='baixarArquivoValidacao(arquivo)'
        ) {{ arquivo.name }}
      p.aviso-campo(v-else) Nenhum documento informado
      p.erro-validacao(v-if='erros.arquivos') {{ erros.arquivos }}

    el-divider

    //- Resultado
    .secao
      radio-button-component(
        label='Resultado da validação *'
        :list='opcoesResultado'
        :disabled='modoLeitura'
        @input='onResultadoChange'
      )
      p.erro-validacao(v-if='erros.resultado') {{ erros.resultado }}

    //- Novo prazo (somente REPROVADO)
    .secao(v-if='resultado === "REPROVADO"')
      span.secao-titulo Novo prazo *
      .campo-somente-leitura(v-if='modoLeitura') {{ novoPrazoLabel }}
      el-select(v-else v-model='novoPrazoAnos' placeholder='Selecione o novo prazo' style='width: 200px; margin-top: 8px;')
        el-option(
          v-for='n in 20'
          :key='n'
          :label='n === 1 ? "1 ano" : n + " anos"'
          :value='n'
        )
      p.erro-validacao(v-if='erros.novoPrazo') {{ erros.novoPrazo }}

    el-divider

    //- Parecer
    .secao
      text-area-input-component(
        v-model='parecer'
        label='Parecer do analista *'
        placeholder='Descreva o parecer da validação (máx. 1000 caracteres)'
        :disabled='modoLeitura'
      )
      p.erro-validacao(v-if='erros.parecer') {{ erros.parecer }}

    el-divider(v-if='showPdfGerado')

    .secao(v-if='showPdfGerado')
      span.secao-titulo PDF gerado
      button.arquivo-link(type='button' @click='abrirPdfGerado') {{ arquivoPdfGerado.name }}

    //- Rodapé
    .modal-footer
      button-component(
        label='Cancelar'
        icon='mdi-close'
        backgroundColor='var(--primary-gray)'
        color='var(--primary-light-smoke)'
        @click.native='$emit("close")'
      )
      button-component(
        v-if='!modoLeitura'
        label='Finalizar'
        icon='mdi-check'
        :disabled='isSaving'
        @click.native='finalizar'
      )

  //- Sub-modal de técnicos (renderizado fora do scroll para z-index correto)
  modal-vinculacao-tecnico-validacao-component(
    v-if='showModalTecnicos'
    :tecnicosSelecionados='tecnicosVinculados'
    @vincular='onVincular'
    @close='showModalTecnicos = false'
  )
</template>

<script>
import { buscarDadosModalValidacao, finalizarValidacao } from '@/services/monitoramento.service';
import { downloadDocumento as baixarDocumentoPorCaminho } from '@/services/processo.service';
import ModalVinculacaoTecnicoValidacaoComponent from '@/views/monitoramento/components/ModalVinculacaoTecnicoValidacao.vue';
import BotaoUploadComponent from '@/components/ButtonUploadComponent.vue';

export default {
    name: 'ModalValidacaoMonitoramento',

    components: {
        ModalVinculacaoTecnicoValidacaoComponent,
        BotaoUploadComponent,
    },

    props: {
        monitoramento: {
            type: Object,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isLoading: false,
            isSaving: false,
            dados: {},
            tecnicosVinculados: [],
            arquivos: [],
            fileListDisplay: [],
            resultado: '',
            novoPrazoAnos: null,
            parecer: '',
            showModalTecnicos: false,
            erros: {
                tecnicos: '',
                arquivos: '',
                resultado: '',
                novoPrazo: '',
                parecer: '',
            },
        };
    },

    computed: {
        uploadDadosExtra() {
            return {};
        },

        modoLeitura() {
            return this.readOnly || ['APROVADO', 'REPROVADO'].includes(this.monitoramento.statusValidacao);
        },

        arquivoPdfGerado() {
            const caminhos = [
                this.dados?.urlPdfGerado,
                this.dados?.linkPdfGerado,
                this.dados?.caminhoPdfGerado,
                this.dados?.caminhoArquivoPdfGerado,
                this.dados?.caminhoPdf,
                this.dados?.caminhoArquivoPdf,
                this.dados?.pdfGerado?.url,
                this.dados?.pdfGerado?.link,
                this.dados?.pdfGerado?.caminhoArquivo,
                this.dados?.arquivoPdfGerado?.url,
                this.dados?.arquivoPdfGerado?.link,
                this.dados?.arquivoPdfGerado?.caminhoArquivo,
                this.dados?.arquivoPdf?.url,
                this.dados?.arquivoPdf?.link,
                this.dados?.arquivoPdf?.caminhoArquivo,
                this.dados?.documentoPdfGerado?.url,
                this.dados?.documentoPdfGerado?.link,
                this.dados?.documentoPdfGerado?.caminhoArquivo,
            ].filter(Boolean);

            const nomes = [
                this.dados?.nomePdfGerado,
                this.dados?.nomeArquivoPdfGerado,
                this.dados?.pdfGerado?.nomeArquivo,
                this.dados?.arquivoPdfGerado?.nomeArquivo,
                this.dados?.arquivoPdf?.nomeArquivo,
                this.dados?.documentoPdfGerado?.nomeArquivo,
            ].filter(Boolean);

            if (caminhos.length === 0) {
                return null;
            }

            return {
                caminhoArquivo: caminhos[0],
                name: nomes[0] || 'validacao-monitoramento.pdf',
            };
        },

        showPdfGerado() {
            return this.resultado === 'APROVADO' && !!this.arquivoPdfGerado;
        },

        prazoOriginalLabel() {
          return this.formatarDataBr(this.dados.prazoMonitoramento || this.dados.dataFimMonitoramento);
        },

        tituloModal() {
            return this.modoLeitura ? 'Visualizar Validação do Monitoramento' : 'Validar Monitoramento';
        },

        novoPrazoLabel() {
            if (!this.novoPrazoAnos) {
                return this.dados.novoPrazoMonitoramento || this.dados.dataNovoPrazoMonitoramento || '—';
            }

            return this.novoPrazoAnos === 1 ? '1 ano' : `${this.novoPrazoAnos} anos`;
        },

        opcoesResultado() {
            return [
                { id: 'APROVADO', label: 'Aprovado', checked: this.resultado === 'APROVADO' },
                { id: 'REPROVADO', label: 'Reprovado', checked: this.resultado === 'REPROVADO' },
            ];
        },
    },

    async mounted() {
        this.isLoading = true;
        try {
            const resp = await buscarDadosModalValidacao(this.monitoramento.idMonitoramento);
            this.dados = resp;

            // Pré-preencher dados de validação anterior (se existir)
            if (resp.tecnicos && resp.tecnicos.length > 0) {
                this.tecnicosVinculados = resp.tecnicos.map(t => ({
                    id: t.idUsuario,
                    nome: t.nomeTecnico,
                }));
            }
            if (resp.resultado) {
                this.resultado = resp.resultado;
            }
            this.novoPrazoAnos = this.getNovoPrazoAnosSalvo(resp);
            if (resp.parecer) {
                this.parecer = resp.parecer;
            }
            if (resp.arquivos && resp.arquivos.length > 0) {
                this.arquivos = resp.arquivos.map(a => ({
                    nomeArquivo: a.nomeArquivo,
                    caminhoArquivo: a.caminhoArquivo,
                }));
                this.fileListDisplay = resp.arquivos.map(a => ({
                    name: a.nomeArquivo,
                    caminhoArquivo: a.caminhoArquivo,
                }));
            }
        } catch (e) {
            this.$notify({ title: 'Erro', message: 'Erro ao carregar dados do monitoramento.', type: 'error' });
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
      formatarDataBr(data) {
        if (!data) {
          return '—';
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
          return data;
        }

        const dataNormalizada = new Date(`${data}T00:00:00`);
        if (Number.isNaN(dataNormalizada.getTime())) {
          return data;
        }

        return dataNormalizada.toLocaleDateString('pt-BR');
      },

        getNovoPrazoAnosSalvo(resp) {
            if (!resp || resp.resultado !== 'REPROVADO') return null;

            const valorSalvo = [
                resp.novoPrazoAnos,
                resp.quantidadeAnosNovoPrazo,
                resp.novoPrazoMonitoramentoAnos,
            ].find(value => Number.isInteger(Number(value)) && Number(value) > 0);

            if (valorSalvo) {
                return Number(valorSalvo);
            }

            const novoPrazoMonitoramento = resp.novoPrazoMonitoramento || resp.dataNovoPrazoMonitoramento;
            if (!novoPrazoMonitoramento || !resp.dataFimMonitoramento) {
                return null;
            }

            const dataBase = new Date(`${resp.dataFimMonitoramento}T00:00:00`);
            const dataNovoPrazo = new Date(`${novoPrazoMonitoramento}T00:00:00`);

            if (Number.isNaN(dataBase.getTime()) || Number.isNaN(dataNovoPrazo.getTime())) {
                return null;
            }

            const anos = dataNovoPrazo.getFullYear() - dataBase.getFullYear();
            return anos > 0 ? anos : null;
        },

        onResultadoChange(value) {
            if (this.modoLeitura) {
                return;
            }

            this.resultado = value;
            this.erros.resultado = '';
            if (value !== 'REPROVADO') {
                this.novoPrazoAnos = null;
                this.erros.novoPrazo = '';
            }
        },

        onFileUpload(response) {
            if (this.modoLeitura) {
                return;
            }

            if (response && response.nomeOriginal) {
                this.arquivos.push({ nomeArquivo: response.nomeOriginal, caminhoArquivo: response.nomeGerado, temporario: response.temporario });
                this.erros.arquivos = '';
            }
        },

        onFileRemoved(fileOrResponse) {
            if (this.modoLeitura) {
                return;
            }

            const caminho = fileOrResponse.nomeGerado
        || (fileOrResponse.response && fileOrResponse.response.nomeGerado);
            if (caminho) {
                this.arquivos = this.arquivos.filter(a => a.caminhoArquivo !== caminho);
            }
        },

        onVincular(tecnicos) {
            if (this.modoLeitura) {
                return;
            }

            this.tecnicosVinculados = tecnicos.map(t => ({ id: t.id, nome: t.nomeTecnico }));
            this.erros.tecnicos = '';
        },

        removerTecnico(idTecnico) {
            if (this.modoLeitura) {
                return;
            }

            this.tecnicosVinculados = this.tecnicosVinculados.filter(tecnico => tecnico.id !== idTecnico);
        },

        validar() {
            let valido = true;
            this.erros = { tecnicos: '', arquivos: '', resultado: '', novoPrazo: '', parecer: '' };

            if (this.tecnicosVinculados.length === 0) {
                this.erros.tecnicos = 'É necessário ao menos um técnico para adicionar ao processo!';
                valido = false;
            }
            if (this.arquivos.length === 0) {
                this.erros.arquivos = 'Ao menos um documento deve ser informado.';
                valido = false;
            }
            if (!this.resultado) {
                this.erros.resultado = 'O resultado da validação é obrigatório.';
                valido = false;
            }
            if (this.resultado === 'REPROVADO' && !this.novoPrazoAnos) {
                this.erros.novoPrazo = 'O novo prazo é obrigatório quando o resultado é Reprovado.';
                valido = false;
            }
            if (!this.parecer || !this.parecer.trim()) {
                this.erros.parecer = 'O parecer do analista é obrigatório.';
                valido = false;
            }

            return valido;
        },

        calcularNovoPrazo() {
            if (!this.novoPrazoAnos) return null;
            const base = this.dados.dataFimMonitoramento
                ? new Date(this.dados.dataFimMonitoramento + 'T00:00:00')
                : new Date();

            base.setFullYear(base.getFullYear() + this.novoPrazoAnos);
            return base.toISOString().split('T')[0]; // YYYY-MM-DD
        },

        async baixarArquivoValidacao(arquivo) {
            if (!arquivo?.caminhoArquivo) {
                this.$notify({
                    title: 'Erro',
                    message: 'Arquivo indisponível para download.',
                    showClose: true,
                    type: 'error',
                });
                return;
            }

            try {
                const response = await baixarDocumentoPorCaminho(arquivo.caminhoArquivo);
                const file = window.URL.createObjectURL(new Blob([response.data]));
                const docUrl = document.createElement('a');
                docUrl.href = file;
                docUrl.setAttribute('download', arquivo.name || 'documento');
                document.body.appendChild(docUrl);
                docUrl.click();
                document.body.removeChild(docUrl);
                window.URL.revokeObjectURL(file);
            } catch (error) {
                this.$notify({
                    title: 'Erro',
                    message: 'Erro ao realizar o download do documento.',
                    showClose: true,
                    type: 'error',
                });
            }
        },

        abrirPdfGerado() {
            if (!this.arquivoPdfGerado) {
                return;
            }

            if (/^https?:\/\//i.test(this.arquivoPdfGerado.caminhoArquivo)) {
                window.open(this.arquivoPdfGerado.caminhoArquivo, '_blank');
                return;
            }

            this.baixarArquivoValidacao(this.arquivoPdfGerado);
        },

        async finalizar() {
            if (this.modoLeitura) return;

            if (!this.validar()) return;

            this.isSaving = true;
            try {
                const payload = {
                    idMonitoramento: this.monitoramento.idMonitoramento,
                    idsTecnico: this.tecnicosVinculados.map(t => t.id),
                    resultado: this.resultado,
                    parecer: this.parecer,
                    novoPrazoMonitoramento: this.resultado === 'REPROVADO' ? this.calcularNovoPrazo() : null,
                    arquivos: this.arquivos,
                };

                await finalizarValidacao(payload);

                this.$notify({
                    title: 'Sucesso!',
                    message: 'Validação finalizada com sucesso.',
                    showClose: true,
                    type: 'success',
                });

                this.$emit('finalizado');
            } catch (e) {
                const mensagem = e.response?.data?.message || 'Erro ao finalizar a validação.';
                this.$notify({ title: 'Erro', message: mensagem, showClose: true, type: 'error' });
            } finally {
                this.isSaving = false;
            }
        },
    },
};
</script>

<style scoped>
#modal-validacao-monitoramento {
  padding: 16px 24px;
  max-height: 80vh;
  overflow-y: auto;
}

.dados-monitoramento {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
  background: #f0f9f0;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 4px;
}

.dado-item {
  display: flex;
  flex-direction: column;
}

.dado-label {
  font-size: 11px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dado-valor {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.secao {
  margin: 12px 0;
}

.campo-somente-leitura {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  color: #606266;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.lista-arquivos {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.arquivo-link {
  padding: 0;
  border: none;
  background: transparent;
  color: #409eff;
  text-decoration: none;
  text-align: left;
  cursor: pointer;
}

.arquivo-link:hover {
  text-decoration: underline;
}

.secao-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.secao-titulo {
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
  display: block;
}

.tecnicos-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.tecnico-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid #45b836;
  border-radius: 6px;
  background: #45b836;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.tecnico-badge__nome {
  line-height: 1.2;
}

.tecnico-badge__remover {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.tecnico-badge__remover:hover {
  background: rgba(255, 255, 255, 0.18);
}

.tecnico-badge__remover i {
  font-size: 12px;
  font-weight: 700;
}

.aviso-campo {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.erro-validacao {
  color: #f56c6c;
  font-size: 12px;
  margin: 4px 0 0;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}
</style>
